import { mat4, vec3 } from 'gl-matrix';
import { Context } from '../context/Context';
import { Light, LightCamera, LightType, SHADOW_MAP_DIMENSIONS, BaseLightProperties } from './Light';
import { CascadedShadowMap } from './CascadedShadowMap';

export type DirectionalLightProperties = BaseLightProperties & {
  direction: vec3 | number[];
};

export class DirectionalLight extends Light {
  public readonly type: LightType.Directional = LightType.Directional;

  public direction: vec3 | number[];
  public volumetricDensity: number = 0.05;
  public volumetricScattering: number = 0.01;

  public cascadedShadowMap?: CascadedShadowMap;
  declare public camera: DirectionalLightCamera;

  constructor(properties: DirectionalLightProperties) {
    super(properties);
    this.direction = properties.direction;
    this.camera = new DirectionalLightCamera(SHADOW_MAP_DIMENSIONS, this);
    this.camera.setup();
  }

  /** Directional lights have no spatial bounds. */
  public updateBounds(): void {}

  public setShadowMapResolution(ctx: Context, size: number): void {
    if (this.cascadedShadowMap) {
      this.cascadedShadowMap.destroy();
      this.cascadedShadowMap = undefined;
    }
    this._shadowMapSize = size;
    this.shadowDirty = true;
  }

  private _shadowMapSize = 2048;

  public renderShadowMap(ctx: Context): void {
    const scene = ctx.engine.activeScene!;
    const im = scene.instanceManager;

    if (!this.cascadedShadowMap) {
      this.cascadedShadowMap = new CascadedShadowMap(this._shadowMapSize);
      this.cascadedShadowMap.init(ctx);
    }
    this.cascadedShadowMap.instanceManager = im;
    // Forward LOD distance scale from the engine's graphics settings
    this.cascadedShadowMap.lodDistanceScale =
      ctx.engine.graphicsSettings?.lodDistanceScale ?? 1.0;
    this.cascadedShadowMap.computeAndRender(
      ctx,
      scene,
      ctx.engine.activeCamera,
      this.direction as vec3,
    );
  }

  public packGpuData(data: Float32Array, o: number): void {
    this.packCommon(data, o);
    data[o + 7] = 0; // outerRange² unused for directional (infinite range)

    // Texel 2: direction
    data[o + 8] = this.direction[0] as number;
    data[o + 9] = this.direction[1] as number;
    data[o + 10] = this.direction[2] as number;
    data[o + 11] = 0;

    // Texel 3: ranges (unused for directional; cos(180°) = -1)
    data[o + 12] = 0;
    data[o + 13] = 0;
    data[o + 14] = -1;
    data[o + 15] = -1;
  }
}

export class DirectionalLightCamera extends LightCamera<DirectionalLight> {
  protected determineTargetPosition(): vec3 {
    const target = vec3.create();
    vec3.scale(target, this.light.direction as vec3, 100);
    vec3.add(target, target, this.light.position as vec3);
    return target;
  }

  protected determineUp(): vec3 | number[] {
    return this.computeNonParallelUp();
  }

  protected determineProjection(): mat4 {
    const matrix = mat4.create();
    this.applyOrtho(matrix, 20);
    return matrix;
  }
}
